import { useEffect, useRef, type RefObject } from 'react';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Open modals, innermost last: only the innermost takes Escape and keeps Tab (a confirm dialog over a drawer). */
const stack: { surface: RefObject<HTMLElement | null> }[] = [];

/**
 * Live regions stay outside the hiding: a toast raised while a modal is open must still be announced. `data-nyk-live`
 * marks a wrapper of live regions (the toasts' status and alert regions).
 */
const LIVE = '[aria-live], [role="status"], [role="alert"], [data-nyk-live]';
const SKIP = new Set(['SCRIPT', 'STYLE', 'TEMPLATE', 'LINK']);

/**
 * Hides everything but `el` from assistive technology and input (`aria-hidden` + `inert` on the siblings of `el` and of
 * each of its ancestors up to `body`), so a screen reader cannot wander behind a modal. Returns the undo. Elements that
 * were hidden already are left alone, so nested modals undo in any order; `keep` (modals opened later) are never hidden.
 */
export function hideOthers(el: HTMLElement, keep: HTMLElement[] = []): () => void {
  const changed: HTMLElement[] = [];
  for (let node: HTMLElement | null = el; node && node !== document.body; node = node.parentElement) {
    const parent: HTMLElement | null = node.parentElement;
    if (!parent) break;
    for (const sib of parent.children) {
      if (sib === node || !(sib instanceof HTMLElement) || SKIP.has(sib.tagName)) continue;
      if (sib.getAttribute('aria-hidden') === 'true' || sib.hasAttribute('inert')) continue;
      // a modal opened after this one (its portal mounted first, before this one's delayed focus) stays usable
      if (keep.some((k) => sib.contains(k))) continue;
      // A kit portal holding only the toasts (or a bare live region) stays readable.
      if (sib.matches(LIVE) || (sib.children.length === 1 && sib.firstElementChild!.matches(LIVE))) continue;
      sib.setAttribute('aria-hidden', 'true');
      sib.setAttribute('inert', '');
      changed.push(sib);
    }
  }
  return () => {
    for (const sib of changed) {
      sib.removeAttribute('aria-hidden');
      sib.removeAttribute('inert');
    }
  };
}

/**
 * Keyboard and focus of a modal surface (WCAG 2.2: 2.1.2, 2.4.3, 2.4.11): focus moves into it when it opens, Tab and
 * Shift+Tab stay inside it, Escape closes it (and goes no further), the page behind it is hidden from screen readers
 * (`hideOthers`), and focus returns to what opened it (or to the modal underneath when that is gone).
 */
export function useModalFocus(open: boolean, ref: RefObject<HTMLElement | null>, onClose: () => void) {
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement as HTMLElement | null;
    const token = { surface: ref };
    stack.push(token);
    const onKey = (e: KeyboardEvent) => {
      if (stack[stack.length - 1] !== token) return;
      if (e.key === 'Escape') {
        // Consumed: a page-level handler (a map leaving its drawing mode) must not act on it too.
        e.preventDefault();
        e.stopPropagation();
        closeRef.current();
        return;
      }
      if (e.key !== 'Tab' || !ref.current) return;
      const focusable = [...ref.current.querySelectorAll<HTMLElement>(FOCUSABLE)];
      if (focusable.length === 0) {
        e.preventDefault();
        ref.current.focus();
        return;
      }
      const [first, last] = [focusable[0], focusable[focusable.length - 1]];
      if (e.shiftKey && (document.activeElement === first || document.activeElement === ref.current)) {
        e.preventDefault();
        last.focus();
      } else if (
        !e.shiftKey &&
        (document.activeElement === last || !ref.current.contains(document.activeElement))
      ) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    // A portal mounts its content a render later: focus once it is there.
    let tries = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let reveal: (() => void) | undefined;
    const focusIn = () => {
      if (ref.current) {
        const above = stack
          .slice(stack.indexOf(token) + 1)
          .flatMap((t) => (t.surface.current ? [t.surface.current] : []));
        reveal = hideOthers(ref.current, above);
        if (!ref.current.contains(document.activeElement)) ref.current.focus();
      } else if (tries++ < 20) {
        timer = setTimeout(focusIn, 0);
      }
    };
    focusIn();
    return () => {
      clearTimeout(timer);
      reveal?.();
      stack.splice(stack.indexOf(token), 1);
      document.removeEventListener('keydown', onKey);
      if (opener && opener !== document.body && document.contains(opener)) opener.focus();
      // The opener went away while the modal was open (a drawer re-rendered by its save): focus the modal still open
      // underneath, never the page behind it.
      else stack[stack.length - 1]?.surface.current?.focus();
    };
  }, [open, ref]);
}
